/**
 * Générateur — Attendu 968 — « Connaitre les propriétés caractéristiques des
 * parallélogrammes particuliers. » (libellé officiel complet, BO — notre
 * référentiel interne le tronque en « Connaitre les propriétés
 * caractéristiques », `ecarts_assumes` 1.)
 *
 * Niveau : 5ème | Domaine : Espace et géométrie / Parallélogrammes
 * Mise en conformité avec la spécification `Attendu-5e/specifications-tanoed/5e/968.json`
 * (campagne 5e, 2026-09-21). La spécification est la seule référence.
 *
 * ── Plan de cartes (version finale) ──────────────────────────────────────────
 *
 *  Objectif de CONNAISSANCE : aucun instrument, aucune construction, aucune
 *  tolérance de validation (`objets_geometriques` 7, exclusion 5). Trois
 *  figures et trois seulement — rectangle, losange, carré — toujours
 *  présentées comme des parallélogrammes auxquels s'ajoute une propriété.
 *
 *  1. caracterisation — niveaux 1 à 3, voie des DIAGONALES : que peut-on
 *                       affirmer à coup sûr ? Sélection multiple.
 *  2. cotes_angles    — niveaux 1 à 3, voie des CÔTÉS ET DES ANGLES : la
 *                       seconde voie vers chaque figure.
 *  3. insuffisant     — niveau 4 : les données ne permettent aucune conclusion,
 *                       et le dire est la réponse attendue.
 *  4. carte_identite  — niveau 5, sens direct : la figure est donnée, quelles
 *                       propriétés a-t-elle ?
 *  5. demo            — niveau 6 : le raisonnement en trois temps — ce qui est
 *                       donné, la propriété caractéristique citée in extenso,
 *                       la conclusion (`type_reponse`).
 *  6. isocele         — niveau 6 : le même raisonnement sur un schéma codé.
 *  7. statut          — niveau 7 : propriété, propriété caractéristique, ou
 *                       ni l'une ni l'autre.
 *
 * ── Spécification ────────────────────────────────────────────────────────────
 *
 *  Bornes appliquées :
 *   - figures concernées : rectangle, losange, carré, et aucune autre ;
 *   - caractérisations par les diagonales : les 3 de la liste fermée ;
 *   - caractérisations par les côtés et les angles : les 3 de la liste fermée ;
 *     chaque figure reçoit donc au moins deux voies, le carré trois
 *     (`nombre_de_caracterisations_par_figure`) ;
 *   - bonnes réponses par question : 1 à 4 selon la configuration, 0 nature
 *     affirmable sur la carte `insuffisant` (voir Non tranché) ;
 *   - proportion de questions sans conclusion possible : 1 carte de sélection
 *     multiple sur 3, soit 0,33 ;
 *   - angle entre les diagonales du schéma dessiné : entier de 80 à 100
 *     degrés — le schéma ne représente JAMAIS la figure annoncée ;
 *   - hypothèse « quadrilatère non croisé » écrite dans chaque énoncé.
 *  Exclusions : aucune figure hors des trois ; aucun quadrilatère croisé ;
 *   aucun distracteur faux en soi (chaque proposition écartée est la
 *   caractérisation d'une autre figure, ou une propriété vraie mais
 *   insuffisante) ; aucune figure en vraie grandeur ; aucune tâche de
 *   construction.
 *
 *  Erreurs visées → mots-clés (codes des distracteurs, `_meta.params`) :
 *   1. ne voir dans le carré qu'une figure de plus, et non un cas particulier
 *      du losange et du rectangle → `carre_hors_inclusion`
 *   2. conclure d'après le dessin plutôt que d'après le codage
 *      → `conclure_sur_le_dessin` (le schéma est à main levée et contredit
 *        toujours la nature annoncée)
 *   3. n'avoir retenu qu'une seule caractérisation par figure
 *      → `une_seule_voie` (les deux voies sont servies par deux cartes)
 *   4. croire que deux côtés consécutifs de même longueur ne suffisent pas
 *      → `cotes_consecutifs_insuffisants`
 *   5. croire qu'il manque une donnée devant une caractérisation minimale
 *      → `caracterisation_minimale`
 *   6. confondre une condition suffisante et une condition nécessaire
 *      → `suffisant_pour_necessaire` (statut)
 *   7. refuser de ne rien conclure quand les données ne le permettent pas
 *      → `refus_de_ne_rien_conclure` (les quatre natures, sur les
 *        configurations insuffisantes)
 *   8. attribuer au losange une propriété du rectangle
 *      → `losange_pour_rectangle`
 *
 *  Graduation → cartes : 1 à 3 caracterisation et cotes_angles · 4 insuffisant ·
 *   5 carte_identite · 6 demo et isocele · 7 statut.
 *
 *  Non tranché (appliqué tel qu'écrit, signalé dans le rapport) :
 *   - La réponse VIDE, que `nombre_de_bonnes_reponses_par_question` autorise
 *     (min 0) et que la vigilance 1 exige comme réponse valide, n'est pas
 *     représentable : le harnais bloque un QCM multiple à `bonne_reponse`
 *     vide. Une cinquième proposition, « les données ne permettent d'affirmer
 *     aucune de ces natures », la rend cochable ; le geste attendu est donc de
 *     cocher cette ligne et rien d'autre.
 *   - `type_reponse` propose quatre natures dont « parallélogramme », mais
 *     `nombre_de_bonnes_reponses_par_question` plafonne à 3 en comptant, pour
 *     le carré, losange et rectangle seulement. Les deux champs se
 *     contredisent : les quatre natures sont proposées, le carré en rend donc
 *     quatre affirmables.
 *   - Le réglage « inclure ou non les caractérisations par les angles » n'est
 *     pas implémenté : elles sont incluses, comme la liste fermée le prévoit.
 */

import {
    randInt, randomChoice, shuffle, pickLettresSains,
    makeQcm, makeTexteTrou, makeVraiFaux, filtrerDistracteurs,
} from '../../../utils';

// ═══════════════════════════════════════════════════════════
// CONSTANTES
// ═══════════════════════════════════════════════════════════

const ATTENDU_ID = 968;
const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });
const meta = (payload, key, params) => {
    payload._meta.generatorKey = key;
    payload._meta.params = params;
    return payload;
};

const L_CARACT = 'Caractériser par les diagonales';
const L_COTES = 'Caractériser par les côtés et les angles';
const L_INSUFFISANT = 'Quand on ne peut rien conclure';
const L_IDENTITE = 'Carte d\'identité';
const L_DEMO = 'Petite démonstration';
const L_ISOCELE = 'Trois triangles isocèles';
const L_STATUT = 'Propriété ou propriété caractéristique ?';

const ANGLE_DIAG_MIN = 80;
const ANGLE_DIAG_MAX = 100;

const POOL_LETTRES = 'ABCDEFGHJKLMNPRSTUVWYZ'.split('');
const pickLetters = (n) => pickLettresSains(POOL_LETTRES, n);
const NOM_CENTRE = 'O';
const toRad = (d) => (d * Math.PI) / 180;

const E_CARRE = 'carre_hors_inclusion';
const E_DESSIN = 'conclure_sur_le_dessin';
const E_UNE_VOIE = 'une_seule_voie';
const E_COTES_INSUFF = 'cotes_consecutifs_insuffisants';
const E_MINIMALE = 'caracterisation_minimale';
const E_SUFF_NEC = 'suffisant_pour_necessaire';
const E_REFUS = 'refus_de_ne_rien_conclure';
const E_LOSANGE_RECT = 'losange_pour_rectangle';

const NATURES = [
    { id: 'para', choix: 'C\'est un parallélogramme.' },
    { id: 'rectangle', choix: 'C\'est un rectangle.' },
    { id: 'losange', choix: 'C\'est un losange.' },
    { id: 'carre', choix: 'C\'est un carré.' },
];
const AUCUNE = 'Les données ne permettent d\'affirmer aucune de ces natures.';

// ═══════════════════════════════════════════════════════════
// SCHÉMA CODÉ À MAIN LEVÉE
// ═══════════════════════════════════════════════════════════

const VB_FIG = { w: 280, h: 210 };

/**
 * UNE COULEUR PAR CATÉGORIE DE CODAGE (arbitrage prof 2026-09-27) : l'élève voit
 * d'un coup d'œil de quoi parle une marque, sans avoir à identifier sa forme.
 *   longueurs égales → vert    parallélisme → bleu    angle droit → rouge
 * Avant, les marques de longueur étaient tantôt noires, tantôt violettes selon
 * qu'elles portaient sur une demi-diagonale ou sur une diagonale entière, ce qui
 * laissait croire à deux notions différentes.
 */
const COUL_LONGUEUR = '#15803d';
const COUL_PARALLELE = '#1E3A8A';
const COUL_ANGLE = '#E24B4A';

const ticksAt = (P, Q, t, n, couleur = COUL_LONGUEUR) => {
    const cx0 = P.x + t * (Q.x - P.x);
    const cy0 = P.y + t * (Q.y - P.y);
    const dx = Q.x - P.x;
    const dy = Q.y - P.y;
    const L = Math.hypot(dx, dy) || 1;
    const ux = dx / L;
    const uy = dy / L;
    let out = '';
    for (let i = 0; i < n; i++) {
        const s = (i - (n - 1) / 2) * 4.5;
        const cx = cx0 + ux * s;
        const cy = cy0 + uy * s;
        out += `<line x1="${(cx + uy * 5).toFixed(1)}" y1="${(cy - ux * 5).toFixed(1)}" x2="${(cx - uy * 5).toFixed(1)}" y2="${(cy + ux * 5).toFixed(1)}" stroke="${couleur}" stroke-width="1.6"/>`;
    }
    return out;
};

/**
 * Legende du codage de parallelisme, servie SEULEMENT quand la figure en porte.
 * Meme raison qu'au gen 966 : le chevron est repandu en manuel mais aucun texte du
 * programme ne le nomme, et la spec de ce generateur ecrit une autre convention
 * (Â« segments opposes de meme couleur Â»). Tant qu'il porte la reponse, on le dit
 * en clair (arbitrage prof 2026-09-27).
 */
const legendeChevrons = (codage) => ((codage || []).some((c) => c === 'unParallelisme' || c === 'cotesOpposes')
    ? '<p><i>Les chevrons (les petites flèches) marquent des côtés parallèles : deux côtés qui portent le même nombre de chevrons sont parallèles entre eux.</i></p>'
    : '');

const chevronsAt = (P, Q, n) => {
    const mx = (P.x + Q.x) / 2;
    const my = (P.y + Q.y) / 2;
    const dx = Q.x - P.x;
    const dy = Q.y - P.y;
    const L = Math.hypot(dx, dy) || 1;
    const ux = dx / L;
    const uy = dy / L;
    let out = '';
    for (let i = 0; i < n; i++) {
        // Un chevron mesure 8 px de long (de cx - 4 a cx + 4). A 6 px d'ecart, deux
        // chevrons consecutifs se CHEVAUCHAIENT de 2 px : au lieu de deux fleches on
        // lisait une croix emmelee (retour prof 2026-09-27). A 11 px il reste 3 px
        // de blanc entre les deux.
        const s = (i - (n - 1) / 2) * 11;
        const cx = mx + ux * s;
        const cy = my + uy * s;
        out += `<path d="M ${(cx - ux * 4 + uy * 5).toFixed(1)},${(cy - uy * 4 - ux * 5).toFixed(1)} L ${(cx + ux * 4).toFixed(1)},${(cy + uy * 4).toFixed(1)} L ${(cx - ux * 4 - uy * 5).toFixed(1)},${(cy - uy * 4 + ux * 5).toFixed(1)}" fill="none" stroke="${COUL_PARALLELE}" stroke-width="1.6"/>`;
    }
    return out;
};

const angleDroit = (V, P, Q, c = 11) => {
    const u = (T) => {
        const dx = T.x - V.x;
        const dy = T.y - V.y;
        const L = Math.hypot(dx, dy) || 1;
        return { x: (dx / L) * c, y: (dy / L) * c };
    };
    const a = u(P);
    const b = u(Q);
    return `<path d="M ${(V.x + a.x).toFixed(1)},${(V.y + a.y).toFixed(1)} L ${(V.x + a.x + b.x).toFixed(1)},${(V.y + a.y + b.y).toFixed(1)} L ${(V.x + b.x).toFixed(1)},${(V.y + b.y).toFixed(1)}" fill="none" stroke="${COUL_ANGLE}" stroke-width="1.6"/>`;
};

/**
 * Schéma d'un quadrilatère, construit à partir d'UN SEUL couple de
 * demi-diagonales (vigilance 8 : deux sommets opposés tirés séparément ne
 * seraient pas symétriques et la figure ne serait même pas un parallélogramme
 * approché). Les deux demi-diagonales sont de longueurs différentes et leur
 * angle reste entre 80 et 100 degrés : le dessin ne représente donc JAMAIS la
 * figure annoncée, et seul le codage porte l'information
 * (`angle_entre_les_diagonales_de_la_figure_dessinee`, exclusion 4).
 */
const schema = (codage, noms, tirage, { centre = false } = {}) => {
    const { r1, r2, ang, theta } = tirage;
    const O = { x: VB_FIG.w / 2, y: VB_FIG.h / 2 };
    const demi = (r, a) => ({ x: r * Math.cos(toRad(a)), y: -r * Math.sin(toRad(a)) });
    const u = demi(r1, theta);
    const v = demi(r2, theta + ang);
    const S = [
        { x: O.x - u.x, y: O.y - u.y },
        { x: O.x + v.x, y: O.y + v.y },
        { x: O.x + u.x, y: O.y + u.y },
        { x: O.x - v.x, y: O.y - v.y },
    ];
    let g = `<polygon points="${S.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ')}" fill="#f8fafc" stroke="#334155" stroke-width="2"/>`;
    const avecDiagonales = centre || codage.some((c) => c.startsWith('diag') || c === 'quatreDemiDiag');
    if (avecDiagonales) {
        g += `<line x1="${S[0].x.toFixed(1)}" y1="${S[0].y.toFixed(1)}" x2="${S[2].x.toFixed(1)}" y2="${S[2].y.toFixed(1)}" stroke="#64748b" stroke-width="1.4" stroke-dasharray="5 4"/>`
            + `<line x1="${S[1].x.toFixed(1)}" y1="${S[1].y.toFixed(1)}" x2="${S[3].x.toFixed(1)}" y2="${S[3].y.toFixed(1)}" stroke="#64748b" stroke-width="1.4" stroke-dasharray="5 4"/>`;
    }
    codage.forEach((c) => {
        if (c === 'diagMilieu') {
            // Coupees en leur milieu, mais PAS de meme longueur : une marque par
            // diagonale, un trait d'un cote, deux de l'autre.
            g += ticksAt(S[0], O, 0.5, 1) + ticksAt(O, S[2], 0.5, 1)
                + ticksAt(S[1], O, 0.5, 2) + ticksAt(O, S[3], 0.5, 2);
        } else if (c === 'quatreDemiDiag') {
            // LE MEME trait sur les quatre demi-diagonales : elles se coupent en leur
            // milieu ET ont la meme longueur, d'un seul coup.
            //
            // Avant, ces deux faits se codaient separement, et les trois traits violets
            // de `diagEgales` tombaient a 14 % de la diagonale, donc SUR la moitie qui
            // portait deja sa marque : deux codages d'egalite differents sur le meme
            // trait, sans rien qui dise lequel parle de la moitie et lequel de la
            // diagonale entiere (meme defaut qu'au gen 966, arbitrage prof 2026-09-27).
            g += ticksAt(S[0], O, 0.5, 1) + ticksAt(O, S[2], 0.5, 1)
                + ticksAt(S[1], O, 0.5, 1) + ticksAt(O, S[3], 0.5, 1);
        } else if (c === 'diagEgales') {
            // Diagonales de MEME LONGUEUR qui ne se coupent PAS en leur milieu : ici la
            // marque doit bien porter sur la diagonale entiere, et rien d'autre n'est
            // trace dessus. Aucune ambiguite, on garde.
            g += ticksAt(S[0], S[2], 0.14, 3) + ticksAt(S[1], S[3], 0.14, 3);
        } else if (c === 'diagPerp') {
            g += angleDroit(O, S[0], S[1]);
        } else if (c === 'angleDroit') {
            g += angleDroit(S[0], S[1], S[3]);
        } else if (c === 'cotesConsecutifs') {
            g += ticksAt(S[0], S[1], 0.5, 1) + ticksAt(S[0], S[3], 0.5, 1);
        } else if (c === 'cotesOpposes') {
            g += chevronsAt(S[0], S[1], 1) + chevronsAt(S[3], S[2], 1)
                + chevronsAt(S[1], S[2], 2) + chevronsAt(S[0], S[3], 2);
        } else if (c === 'unParallelisme') {
            g += chevronsAt(S[0], S[1], 1) + chevronsAt(S[3], S[2], 1);
        } else if (c === 'deuxOpposesEgaux') {
            g += ticksAt(S[0], S[1], 0.5, 1) + ticksAt(S[3], S[2], 0.5, 1);
        }
    });
    if (centre) {
        g += `<circle cx="${O.x.toFixed(1)}" cy="${O.y.toFixed(1)}" r="2.4" fill="#0f172a"/>`
            // O ne peut pas sortir de la figure, mais il ne doit pas s'asseoir sur une
            // diagonale : on le pose sur la bissectrice d'un des quatre secteurs.
            + (() => {
                const nu = Math.hypot(u.x, u.y) || 1;
                const nv = Math.hypot(v.x, v.y) || 1;
                const bx = -u.x / nu - v.x / nv;
                const by = -u.y / nu - v.y / nv;
                const nb = Math.hypot(bx, by) || 1;
                return `<text x="${(O.x + (bx / nb) * 15).toFixed(1)}" y="${(O.y + (by / nb) * 15 + 5).toFixed(1)}" font-family="serif" font-size="14" font-weight="600" text-anchor="middle">${NOM_CENTRE}</text>`;
            })();
    }
    // Le nom d'un sommet se pose DEHORS, dans la direction qui s'écarte du centre :
    // le quadrilatère étant convexe, cette direction sort toujours de la figure. Les
    // décalages fixes d'avant ([-13, 12], [13, 12]…) ne tenaient aucun compte de
    // l'orientation du schéma, qui change à chaque tirage — les lettres tombaient sur
    // un côté, voire à l'intérieur (retour prof 2026-09-27).
    S.forEach((p, i) => {
        const dx = p.x - O.x;
        const dy = p.y - O.y;
        const n = Math.hypot(dx, dy) || 1;
        const tx = p.x + (dx / n) * 16;
        const ty = p.y + (dy / n) * 16 + 5;
        g += `<circle cx="${p.x.toFixed(1)}" cy="${p.y.toFixed(1)}" r="2.4" fill="#0f172a"/>`
            + `<text x="${tx.toFixed(1)}" y="${ty.toFixed(1)}" font-family="serif" font-size="14" font-weight="600" text-anchor="middle">${noms[i]}</text>`;
    });
    const s = `${noms.join('')}_${codage.join('-')}_${r1}_${r2}_${ang}_${theta}`;
    return `<svg data-v="${s}" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${VB_FIG.w} ${VB_FIG.h}" width="${VB_FIG.w}" height="${VB_FIG.h}">${g}</svg>`;
};

/**
 * Demi-diagonales toujours inégales, angle toujours PROCHE du droit sans jamais
 * l'être : 89, 90 et 91 degrés sont écartés, sans quoi le schéma représenterait
 * exactement le losange qu'il est censé ne jamais représenter.
 */
const tirerSchema = () => {
    const r1 = randInt(72, 88);
    const r2 = randInt(44, 58);
    let ang = randInt(ANGLE_DIAG_MIN, ANGLE_DIAG_MAX);
    for (let i = 0; i < 40 && [89, 90, 91].includes(ang); i++) ang = randInt(ANGLE_DIAG_MIN, ANGLE_DIAG_MAX);
    if ([89, 90, 91].includes(ang)) ang = 83;
    return { r1, r2, ang, theta: randInt(0, 17) * 10 };
};

const imgEnonce = (svg) => ({
    version: 1,
    elements: [{ type: 'image', config: { imageUrl: `data:image/svg+xml,${encodeURIComponent(svg)}`, maxWidth: 'small' } }],
});

const AVERTISSEMENT = '<p><em>Le schéma est tracé à main levée : il n\'est pas en vraie grandeur et '
    + 'ne représente pas la figure annoncée. Seuls le codage et l\'énoncé comptent.</em></p>';
// La consigne disait « si les données ne permettent d'affirmer aucune de ces
// natures, coche la dernière ligne » : elle DÉSIGNAIT une réponse avant même que
// l'élève ait lu l'énoncé, et l'amorçait à la cocher (retour prof 2026-09-27).
// Elle dit maintenant la même règle sans nommer de ligne : il faut cocher quelque
// chose, à l'élève de trouver quoi.
const CONSIGNE = '<p><b>Coche tout ce que tu peux affirmer à coup sûr</b>, et rien de plus. '
    + 'Il y a toujours au moins une ligne à cocher.</p>';

// ═══════════════════════════════════════════════════════════
// LES SIX CARACTÉRISATIONS DE LA SPÉCIFICATION
// ═══════════════════════════════════════════════════════════

/** Voie des diagonales : les trois énoncés de la liste fermée. */
const VOIE_DIAGONALES = [
    {
        id: 'diag_milieu',
        codage: ['diagMilieu'],
        donnees: (n) => `ses diagonales [${n[0]}${n[2]}] et [${n[1]}${n[3]}] se coupent en leur milieu`,
        naturesVraies: ['para'],
        propriete: 'un quadrilatère non croisé dont les diagonales se coupent en leur milieu est un parallélogramme',
    },
    {
        id: 'diag_milieu_egales',
        codage: ['quatreDemiDiag'],
        donnees: (n) => `ses diagonales [${n[0]}${n[2]}] et [${n[1]}${n[3]}] se coupent en leur milieu et ont la même longueur`,
        naturesVraies: ['para', 'rectangle'],
        propriete: 'un parallélogramme dont les diagonales ont la même longueur est un rectangle',
    },
    {
        id: 'diag_milieu_perp',
        codage: ['diagMilieu', 'diagPerp'],
        donnees: (n) => `ses diagonales [${n[0]}${n[2]}] et [${n[1]}${n[3]}] se coupent en leur milieu et sont perpendiculaires`,
        naturesVraies: ['para', 'losange'],
        propriete: 'un parallélogramme dont les diagonales sont perpendiculaires est un losange',
    },
    {
        id: 'diag_milieu_egales_perp',
        codage: ['quatreDemiDiag', 'diagPerp'],
        donnees: (n) => `ses diagonales [${n[0]}${n[2]}] et [${n[1]}${n[3]}] se coupent en leur milieu, ont la même longueur et sont perpendiculaires`,
        naturesVraies: ['para', 'rectangle', 'losange', 'carre'],
        propriete: 'un parallélogramme dont les diagonales ont la même longueur et sont perpendiculaires est un carré',
    },
];

/** Voie des côtés et des angles : les trois énoncés de la liste fermée. */
const VOIE_COTES = [
    {
        id: 'para_seul',
        codage: ['cotesOpposes'],
        donnees: () => 'c\'est un parallélogramme, et rien de plus n\'est donné',
        naturesVraies: ['para'],
        propriete: 'un parallélogramme sans autre propriété reste un parallélogramme quelconque',
    },
    {
        id: 'para_angle_droit',
        codage: ['cotesOpposes', 'angleDroit'],
        donnees: (n) => `c'est un parallélogramme et que son angle de sommet ${n[0]} est droit`,
        naturesVraies: ['para', 'rectangle'],
        propriete: 'un parallélogramme qui a un angle droit est un rectangle',
        erreur: E_MINIMALE,
    },
    {
        id: 'para_cotes_consecutifs',
        codage: ['cotesOpposes', 'cotesConsecutifs'],
        donnees: (n) => `c'est un parallélogramme et que ses côtés consécutifs [${n[0]}${n[1]}] et [${n[0]}${n[3]}] ont la même longueur`,
        naturesVraies: ['para', 'losange'],
        propriete: 'un parallélogramme qui a deux côtés consécutifs de même longueur est un losange',
        erreur: E_COTES_INSUFF,
    },
    {
        id: 'para_angle_droit_cotes',
        codage: ['cotesOpposes', 'angleDroit', 'cotesConsecutifs'],
        donnees: (n) => `c'est un parallélogramme, que son angle de sommet ${n[0]} est droit et que ses côtés consécutifs [${n[0]}${n[1]}] et [${n[0]}${n[3]}] ont la même longueur`,
        naturesVraies: ['para', 'rectangle', 'losange', 'carre'],
        propriete: 'un parallélogramme qui a un angle droit et deux côtés consécutifs de même longueur est un carré',
        erreur: E_CARRE,
    },
];

/** Hypothèses insuffisantes : aucune nature n'est affirmable. */
const INSUFFISANTES = [
    {
        id: 'un_parallelisme',
        codage: ['unParallelisme'],
        donnees: (n) => `ses côtés [${n[0]}${n[1]}] et [${n[3]}${n[2]}] sont parallèles`,
        naturesVraies: [],
        propriete: 'deux côtés opposés parallèles ne suffisent pas : un trapèze en a aussi',
    },
    {
        id: 'deux_opposes_egaux',
        codage: ['deuxOpposesEgaux'],
        donnees: (n) => `ses côtés [${n[0]}${n[1]}] et [${n[3]}${n[2]}] ont la même longueur`,
        naturesVraies: [],
        propriete: 'deux côtés opposés de même longueur ne suffisent pas',
    },
    {
        id: 'diag_perp_sans_milieu',
        codage: ['diagPerp'],
        donnees: (n) => `ses diagonales [${n[0]}${n[2]}] et [${n[1]}${n[3]}] sont perpendiculaires`,
        naturesVraies: [],
        propriete: 'des diagonales perpendiculaires ne suffisent pas : un cerf-volant en a aussi',
    },
    {
        id: 'diag_egales_sans_milieu',
        codage: ['diagEgales'],
        donnees: (n) => `ses diagonales [${n[0]}${n[2]}] et [${n[1]}${n[3]}] ont la même longueur`,
        naturesVraies: [],
        propriete: 'des diagonales de même longueur ne suffisent pas si elles ne se coupent pas en leur milieu',
    },
];

/** Le code de la nature cochée à tort, selon ce que la configuration donne. */
const codeNature = (cfg, id) => {
    if (cfg.naturesVraies.length === 0) return E_REFUS;
    if (id === 'carre') return E_CARRE;
    if (id === 'rectangle') return cfg.naturesVraies.includes('losange') ? E_LOSANGE_RECT : E_DESSIN;
    if (id === 'losange') return E_DESSIN;
    return E_DESSIN;
};

/** Une question à sélection multiple bâtie sur une configuration. */
const questionMultiple = (cfg, label, key, niveau, voie) => {
    const n = pickLetters(4);
    const tirage = tirerSchema();
    const vraies = NATURES.filter((x) => cfg.naturesVraies.includes(x.id));
    const options = [
        ...NATURES.map((x) => ({
            choix: x.choix,
            code: cfg.naturesVraies.includes(x.id) ? null : codeNature(cfg, x.id),
        })),
        { choix: AUCUNE, code: vraies.length ? E_UNE_VOIE : null },
    ];
    const correct = vraies.length ? vraies.map((x) => x.choix) : [AUCUNE];
    const texte = `<p>${n.join('')} est un quadrilatère <b>non croisé</b>. On sait que ${cfg.donnees(n)}.</p>`
        + legendeChevrons(cfg.codage) + CONSIGNE + AVERTISSEMENT;
    const payload = makeQcm(options.map((o) => o.choix), correct, texte, {
        ...opts(label),
        elements_additionnels: imgEnonce(schema(cfg.codage, n, tirage)),
    }, true);
    return meta(payload, key, {
        niveau,
        voie,
        configuration: cfg.id,
        natures_affirmables: cfg.naturesVraies,
        nombre_de_bonnes_reponses_par_question: correct.length,
        nombre_de_propositions_par_question: options.length,
        angle_entre_les_diagonales_de_la_figure_dessinee: tirage.ang,
        hypothese_de_quadrilatere_non_croise: true,
        distracteurs: options.filter((o) => o.code),
        erreursVisees: [E_DESSIN, ...(cfg.erreur ? [cfg.erreur] : [])],
    });
};

const genCaracterisation = () => questionMultiple(
    randomChoice(VOIE_DIAGONALES), L_CARACT, 'caracterisation_qcm_multiple',
    randomChoice([1, 2, 3]), 'diagonales',
);

const genCotesAngles = () => questionMultiple(
    randomChoice(VOIE_COTES), L_COTES, 'cotes_angles_qcm_multiple',
    randomChoice([1, 2, 3]), 'cotes et angles',
);

const genInsuffisant = () => questionMultiple(
    randomChoice(INSUFFISANTES), L_INSUFFISANT, 'insuffisant_qcm_multiple',
    4, 'insuffisante',
);

// ═══════════════════════════════════════════════════════════
// Carte 4 — carte_identite (niveau 5) : le sens direct
// ═══════════════════════════════════════════════════════════

const PROPRIETES = [
    { txt: 'Ses côtés opposés sont de la même longueur.', rectangle: true, losange: true, carré: true, code: null },
    { txt: 'Ses quatre côtés sont de la même longueur.', rectangle: false, losange: true, carré: true, code: E_LOSANGE_RECT },
    { txt: 'Ses quatre angles sont droits.', rectangle: true, losange: false, carré: true, code: E_LOSANGE_RECT },
    { txt: 'Ses diagonales se coupent en leur milieu.', rectangle: true, losange: true, carré: true, code: null },
    { txt: 'Ses diagonales ont la même longueur.', rectangle: true, losange: false, carré: true, code: E_LOSANGE_RECT },
    { txt: 'Ses diagonales sont perpendiculaires.', rectangle: false, losange: true, carré: true, code: E_DESSIN },
    // Les deux lignes suivantes sont FAUSSES pour les trois figures : un rectangle
    // peut être un carré, un losange aussi. Elles garantissent qu'une question
    // n'est jamais « tout vrai » (exclusion 6) et servent la chaîne d'inclusion.
    { txt: 'Ses diagonales ont nécessairement des longueurs différentes.', rectangle: false, losange: false, carré: false, code: E_CARRE, inclusion: true },
    { txt: 'Deux de ses côtés consécutifs ont nécessairement des longueurs différentes.', rectangle: false, losange: false, carré: false, code: E_CARRE, inclusion: true },
];

const genCarteIdentite = () => {
    const forme = randomChoice(['rectangle', 'losange', 'carré']);
    const n = pickLetters(4);
    // Les deux lignes d'inclusion sont toujours présentes : elles sont fausses
    // pour les trois figures, donc aucune question n'est « tout vrai ».
    const lignesData = shuffle([
        ...PROPRIETES.filter((p) => p.inclusion),
        ...shuffle(PROPRIETES.filter((p) => !p.inclusion)).slice(0, 4),
    ]);
    const lignes = lignesData.map((p) => p.txt);
    const bonnes = lignesData.map((p) => (p[forme] ? 0 : 1));   // 0 = Vrai, 1 = Faux
    const texte = `<p>Le quadrilatère non croisé ${n.join('')} est un <b>${forme}</b>. `
        + 'Pour chaque propriété, coche Vrai ou Faux.</p>'
        + '<p><em>Ce sens de lecture est le sens direct : on part de la figure et on en déduit ses '
        + 'propriétés.</em></p>';
    const payload = makeVraiFaux(lignes, bonnes, texte, opts(L_IDENTITE));
    return meta(payload, 'carte_identite_tableau_vf', {
        niveau: 5,
        sens: 'direct',
        figure: forme,
        hypothese_de_quadrilatere_non_croise: true,
        distracteurs: lignesData.filter((p) => !p[forme]).map((p) => ({ choix: p.txt, code: p.code || E_DESSIN })),
        erreursVisees: [E_CARRE],
    });
};

// ═══════════════════════════════════════════════════════════
// Carte 5 — demo (niveau 6) : le raisonnement en trois temps
// ═══════════════════════════════════════════════════════════

const SCENARIOS_DEMO = [
    {
        id: 'rectangle_diagonales',
        hypothese: (n) => `ses diagonales [${n[0]}${n[2]}] et [${n[1]}${n[3]}] ont la même longueur`,
        propriete: 'ses diagonales de la même longueur',
        conclusion: 'un rectangle',
    },
    {
        id: 'losange_diagonales',
        hypothese: (n) => `ses diagonales [${n[0]}${n[2]}] et [${n[1]}${n[3]}] sont perpendiculaires`,
        propriete: 'ses diagonales perpendiculaires',
        conclusion: 'un losange',
    },
    {
        id: 'carre_diagonales',
        hypothese: (n) => `ses diagonales [${n[0]}${n[2]}] et [${n[1]}${n[3]}] ont la même longueur et sont perpendiculaires`,
        propriete: 'ses diagonales de la même longueur et perpendiculaires',
        conclusion: 'un carré',
    },
    {
        id: 'rectangle_angle',
        hypothese: (n) => `son angle de sommet ${n[0]} est droit`,
        propriete: 'un angle droit',
        conclusion: 'un rectangle',
    },
    {
        id: 'losange_cotes',
        hypothese: (n) => `ses côtés consécutifs [${n[0]}${n[1]}] et [${n[0]}${n[3]}] ont la même longueur`,
        propriete: 'deux côtés consécutifs de la même longueur',
        conclusion: 'un losange',
    },
];

const TOUTES_PROPRIETES = [
    'ses diagonales de la même longueur',
    'ses diagonales perpendiculaires',
    'ses diagonales de la même longueur et perpendiculaires',
    'un angle droit',
    'deux côtés consécutifs de la même longueur',
];
const TOUTES_CONCLUSIONS = ['un rectangle', 'un losange', 'un carré'];

const genDemo = () => {
    const n = pickLetters(4);
    const nom = n.join('');
    const sc = randomChoice(SCENARIOS_DEMO);
    // Le raisonnement en trois temps de `type_reponse` : ce qui est donné, la
    // propriété caractéristique citée in extenso, la conclusion.
    const phrase = `Ce qui est donné. ${nom} est un parallélogramme non croisé qui a `
        + `{{${sc.propriete}}}. La propriété caractéristique. Un parallélogramme qui a `
        + `${sc.propriete} est {{${sc.conclusion}}}. La conclusion. Donc ${nom} est `
        + `{{${sc.conclusion}}}.`;
    const distract = filtrerDistracteurs(phrase, [
        ...TOUTES_PROPRIETES.filter((p) => p !== sc.propriete),
        ...TOUTES_CONCLUSIONS.filter((c) => c !== sc.conclusion),
    ]);
    const texte = `<p>${nom} est un parallélogramme non croisé tel que ${sc.hypothese(n)}. `
        + 'Complète le raisonnement en trois temps.</p>';
    const payload = makeTexteTrou(phrase, texte, {
        ...opts(L_DEMO), mode: 'drag', distracteurs: distract,
    });
    return meta(payload, 'demo_texte_trou_drag', {
        niveau: 6,
        sens: 'reciproque',
        scenario: sc.id,
        hypothese_de_quadrilatere_non_croise: true,
        statut_annonce_de_chaque_enonce: 'propriété caractéristique admise',
        distracteurs: distract.map((d) => ({ choix: d, code: TOUTES_CONCLUSIONS.includes(d) ? E_LOSANGE_RECT : E_UNE_VOIE })),
        erreursVisees: [E_SUFF_NEC],
    });
};

// ═══════════════════════════════════════════════════════════
// Carte 6 — isocele (niveau 6) : le même raisonnement sur un schéma
// ═══════════════════════════════════════════════════════════

const genIsocele = () => {
    const n = pickLetters(4);
    const nom = n.join('');
    const tirage = tirerSchema();
    const phrase = `Ce qui est donné. Les triangles ${n[0]}${NOM_CENTRE}${n[3]}, `
        + `${n[0]}${NOM_CENTRE}${n[1]} et ${n[1]}${NOM_CENTRE}${n[2]} sont {{isocèles}}, donc `
        + `${NOM_CENTRE}${n[0]} = ${NOM_CENTRE}${n[1]} = ${NOM_CENTRE}${n[2]} = ${NOM_CENTRE}${n[3]}. `
        + `Les diagonales [${n[0]}${n[2]}] et [${n[1]}${n[3]}] ont donc {{le même milieu}} et `
        + `{{la même longueur}}. La propriété caractéristique. Un quadrilatère non croisé dont les `
        + `diagonales ont le même milieu et la même longueur est {{un rectangle}}. La conclusion. `
        + `Donc ${nom} est {{un rectangle}}.`;
    const distract = filtrerDistracteurs(phrase, [
        'équilatéraux', 'rectangles', 'des longueurs différentes', 'un losange', 'un carré',
    ]);
    const texte = `<p>Sur le schéma, ${NOM_CENTRE} est le point d'intersection des diagonales du `
        + `quadrilatère non croisé ${nom}, et les triangles ${n[0]}${NOM_CENTRE}${n[3]}, `
        + `${n[0]}${NOM_CENTRE}${n[1]} et ${n[1]}${NOM_CENTRE}${n[2]} ont chacun deux angles à la base `
        + `égaux. Complète le raisonnement en trois temps.</p>` + AVERTISSEMENT;
    const payload = makeTexteTrou(phrase, texte, {
        ...opts(L_ISOCELE), mode: 'drag', distracteurs: distract,
        elements_additionnels: imgEnonce(schema([], n, tirage, { centre: true })),
    });
    return meta(payload, 'isocele_texte_trou_drag', {
        niveau: 6,
        sens: 'reciproque',
        figure: 'rectangle',
        angle_entre_les_diagonales_de_la_figure_dessinee: tirage.ang,
        hypothese_de_quadrilatere_non_croise: true,
        statut_annonce_de_chaque_enonce: 'propriété caractéristique admise',
        distracteurs: distract.map((d) => ({ choix: d, code: E_DESSIN })),
        erreursVisees: [E_DESSIN],
    });
};

// ═══════════════════════════════════════════════════════════
// Carte 7 — statut (niveau 7)
// ═══════════════════════════════════════════════════════════

const ENONCES_STATUT = [
    {
        figure: 'rectangle',
        texte: (n) => `Si ${n} est un rectangle, alors ses diagonales se coupent en leur milieu.`,
        statut: 'propriete',
    },
    {
        figure: 'rectangle',
        texte: (n) => `${n} est un parallélogramme non croisé ; si ses diagonales ont la même longueur, alors c'est un rectangle.`,
        statut: 'caracterisation',
    },
    {
        figure: 'rectangle',
        texte: (n) => `Si ${n} est un rectangle, alors ses diagonales sont perpendiculaires.`,
        statut: 'ni',
    },
    {
        figure: 'losange',
        texte: (n) => `Si ${n} est un losange, alors ses côtés opposés sont parallèles.`,
        statut: 'propriete',
    },
    {
        figure: 'losange',
        texte: (n) => `${n} est un parallélogramme non croisé ; si deux de ses côtés consécutifs ont la même longueur, alors c'est un losange.`,
        statut: 'caracterisation',
    },
    {
        figure: 'losange',
        texte: (n) => `Si ${n} est un losange, alors ses diagonales ont la même longueur.`,
        statut: 'ni',
    },
    {
        figure: 'carré',
        texte: (n) => `Si ${n} est un carré, alors c'est à la fois un losange et un rectangle.`,
        statut: 'propriete',
    },
    {
        figure: 'carré',
        texte: (n) => `${n} est un parallélogramme non croisé ; si ses diagonales ont la même longueur et sont perpendiculaires, alors c'est un carré.`,
        statut: 'caracterisation',
    },
];

const OPTIONS_STATUT = (fig) => [
    { id: 'propriete', choix: `C'est une propriété du ${fig} : elle est vraie, mais elle ne suffit pas à reconnaître un ${fig}.`, code: E_SUFF_NEC },
    { id: 'caracterisation', choix: `C'est une propriété caractéristique du ${fig} : elle suffit à le reconnaître, et tout ${fig} la vérifie.`, code: E_SUFF_NEC },
    { id: 'ni', choix: `Ce n'est ni une propriété ni une caractérisation du ${fig} : l'énoncé est faux.`, code: E_LOSANGE_RECT },
];

const genStatutQcm = () => {
    const n = pickLetters(4).join('');
    const e = randomChoice(ENONCES_STATUT);
    const options = OPTIONS_STATUT(e.figure).map((o) => ({
        choix: o.choix,
        code: o.id === e.statut ? null : o.code,
    }));
    const texte = `<p>Voici un énoncé. « ${e.texte(n)} »</p>`
        + '<p>Une propriété est dite <b>caractéristique</b> quand elle et sa réciproque sont vraies '
        + '— elle suffit alors à reconnaître la figure. Quel est le statut de cet énoncé ?</p>'
        + '<p><em>En classe de 5e, ces propriétés sont admises : on ne les démontre pas.</em></p>';
    const bonne = options.find((o) => o.code === null);
    const payload = makeQcm(options.map((o) => o.choix), bonne.choix, texte, opts(L_STATUT));
    return meta(payload, 'statut_qcm_unique', {
        niveau: 7,
        figure: e.figure,
        statut_annonce_de_chaque_enonce: e.statut,
        statut_de_demonstration: 'admise',
        nombre_de_propositions_par_question: options.length,
        nombre_de_bonnes_reponses_par_question: 1,
        distracteurs: options.filter((o) => o.code),
        erreursVisees: [E_SUFF_NEC],
    });
};

// ═══════════════════════════════════════════════════════════
// REGISTRY & EXPORTS
// ═══════════════════════════════════════════════════════════

const GENERATORS = {
    caracterisation_qcm_multiple: genCaracterisation,
    cotes_angles_qcm_multiple: genCotesAngles,
    insuffisant_qcm_multiple: genInsuffisant,
    carte_identite_tableau_vf: genCarteIdentite,
    demo_texte_trou_drag: genDemo,
    isocele_texte_trou_drag: genIsocele,
    statut_qcm_unique: genStatutQcm,
};

const FORMAT_LABELS = {
    tableau_vf: 'Vrai/Faux', qcm_unique: 'QCM', qcm_multiple: 'QCM Multi',
    texte_trou_drag: 'Menus',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'caracterisation', label: L_CARACT, formatsList: buildFormats('caracterisation') },
    { id: 'cotes_angles', label: L_COTES, formatsList: buildFormats('cotes_angles') },
    { id: 'insuffisant', label: L_INSUFFISANT, formatsList: buildFormats('insuffisant') },
    { id: 'carte_identite', label: L_IDENTITE, formatsList: buildFormats('carte_identite') },
    { id: 'demo', label: L_DEMO, formatsList: buildFormats('demo') },
    { id: 'isocele', label: L_ISOCELE, formatsList: buildFormats('isocele') },
    { id: 'statut', label: L_STATUT, formatsList: buildFormats('statut') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};

export const _internals = {
    VOIE_DIAGONALES, VOIE_COTES, INSUFFISANTES, PROPRIETES, ENONCES_STATUT,
    SCENARIOS_DEMO, schema, tirerSchema,
};
